import { P } from '@piccolohealth/util';
import React, { useState } from 'react';
import { off, on } from '../utils/events';
import type { DropAreaBond, DropAreaOptions, DropAreaState } from './useDrop';
import { useMountedState } from './useMountedState';

const createProcess =
  (options: DropAreaOptions, mounted: boolean) =>
  (dataTransfer: DataTransfer, event: React.DragEvent<Element> | React.ClipboardEvent<Element>) => {
    const uri = dataTransfer.getData('text/uri-list');

    if (uri) {
      (options.onUri || P.noop)(uri, event);
      return;
    }

    if (dataTransfer.files?.length) {
      (options.onFiles || P.noop)(Array.from(dataTransfer.files), event);
      return;
    }

    if (dataTransfer.items?.length) {
      dataTransfer.items[0].getAsString((text) => {
        if (mounted) {
          (options.onText || P.noop)(text, event);
        }
      });
    }
  };

const createBond = (
  process: (
    dataTransfer: DataTransfer,
    event: React.DragEvent<Element> | React.ClipboardEvent<Element>,
  ) => void,
  setOver: (over: boolean) => void,
): DropAreaBond => ({
  onDragOver: (event) => {
    event.preventDefault();
  },
  onDragEnter: (event) => {
    event.preventDefault();
    setOver(true);
  },
  onDragLeave: () => {
    setOver(false);
  },
  onDrop: (event) => {
    event.preventDefault();
    event.persist();
    setOver(false);
    process(event.dataTransfer, event);
  },
  onPaste: (event) => {
    event.persist();
    process(event.clipboardData, event);
  },
});

const useDropAreaRu = (options: DropAreaOptions = {}): [DropAreaBond, DropAreaState] => {
  const isMounted = useMountedState();
  const [over, setOver] = useState<boolean>(false);
  const process = React.useMemo(() => createProcess(options, isMounted()), [options, isMounted]);
  const bond: DropAreaBond = React.useMemo(() => createBond(process, setOver), [process]);

  return [bond, { over }];
};

export const useDropArea = (options?: DropAreaOptions | undefined) => {
  const [dropAreaProps, state] = useDropAreaRu(options);

  // https://github.com/streamich/react-use/issues/2368
  const onDragLeave = React.useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      if (!(event.relatedTarget instanceof Node)) return;
      if (event.currentTarget.contains(event.relatedTarget)) return;

      dropAreaProps.onDragLeave(event);
    },
    [dropAreaProps],
  );

  // Global paste event
  const onPaste = React.useCallback(
    (e: React.ClipboardEvent) => {
      const files = e.clipboardData.files;

      if (files.length && options?.onFiles) {
        options.onFiles(Array.from(files), e);
      }
    },
    [options],
  );

  React.useEffect(() => {
    on(document, 'paste', onPaste);
    return () => {
      off(document, 'paste', onPaste);
    };
  }, [onPaste]);

  return [{ ...dropAreaProps, onDragLeave }, state] as const;
};
